// @flow
import {ActionsObservable, ofType} from 'redux-observable';
import {catchError, concatMap, map, switchMap} from 'rxjs/operators';
import {
  ActionCreator,
  buildCatchError,
  createReducer,
} from '../../../store/utils';
import {
  addPaymentMethodApi,
  updatePaymentMethodApi,
  deletePaymentMethodApi,
  getPaymentMethodsApi,
  getPaymentMethodApi,
  updateContinueToChatApi,
  endpoints,
  updateMercadoPagoCheckoutProApi,
  updateIziPayApi,
  updateCulqiApi,
  updateStripeApi,
  updatePaypalApi,
  updateTransbankApi,
  updateNiubizPayLinkApi,
  updateNiubizWebApi,
  updatePayOnDeliveryApi,
} from './api';
import {buildRoute} from '../../../http';

import type {Action} from '../../../store/utils';
import type {
  PaymentMethod,
  StoredFile,
  ClientParameters,
} from '../../../entities';
import {GetSessionSuccess, LoginSuccess} from '../../auth/duck';
import {uploadImageStoreApi} from '../../file-storage/api';
import {createMultiFieldFormData} from '../../../utils/form';
import {of} from 'rxjs';

export type PaymentMethodRequest = {
  enabled?: boolean,
  displayName?: string,
  fileId?: string,
  clientParameters: ClientParameters,
  logoId?: string,
  id?: string,
};

export type MercadoPagoCheckoutProRequest = PaymentMethodRequest & {
  currency?: string,
  serverParameters?: {
    publicKey?: string,
    accessToken?: string,
  },
};
export type IziPayRequest = PaymentMethodRequest & {
  currency?: string,
  serverParameters?: {
    publicKey?: string,
    accessToken?: string,
  },
  clientParameters?: {
    description: string,
  },
};
export type CulqiRequest = PaymentMethodRequest & {
  currency?: string,
  serverParameters?: {
    publicKey?: string,
    accessToken?: string,
  },
  clientParameters?: {
    description: string,
  },
};

export type StripeRequest = {
  currency?: string,
  serverParameters?: {
    publicKey?: string,
    secretKey?: string,
  },
  clientParameters?: {
    description: string,
  },
};

export type PaypalRequest = {
  currency?: string,
  serverParameters?: {
    clientId?: string,
    clientSecret?: string,
  },
  clientParameters?: {
    description: string,
  },
};

export type TransbankRequest = {
  currency?: string,
  serverParameters?: {
    commerceCode?: string,
  },
};

export type NiubizRequest = {
  currency?: string,
  serverParameters?: {
    commerceCode?: string,
    username?: string,
    password?: string,
  },
};

export type PaymentMethodAction = Action & {
  paymentMethod?: PaymentMethod,
  mercadoPagoCheckoutProRequest?: MercadoPagoCheckoutProRequest,
  iziPayRequest?: IziPayRequest,
  culqiRequest?: CulqiRequest,
  stripeRequest?: StripeRequest,
  paypalRequest?: PaypalRequest,
  niubizRequest?: NiubizRequest,
  logoPaymentMethodRequest?: File,
  qrCodePaymentMethodRequest?: File,
  paymentMethodRequest?: PaymentMethodRequest,
};

export type PaymentMethodConfigState = {
  isFetching?: boolean,
  isDeleting?: boolean,
  isSaving?: boolean,
  error?: string,
  updateSuccess?: boolean,
  deleteSuccess?: boolean,
  addSuccess?: boolean,
  paymentMethods?: PaymentMethod[],
  paymentMethod?: PaymentMethod,
};

export class UpdatePaymentMethod extends ActionCreator<PaymentMethodAction> {}
export class UpdatePaymentMethodSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdatePaymentMethodErrorFn extends ActionCreator<PaymentMethodAction> {}

export class UpdateMercadoPagoCheckoutPro extends ActionCreator<PaymentMethodAction> {}
export class UpdateMercadoPagoCheckoutProSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdateMercadoPagoCheckoutProErrorFn extends ActionCreator<PaymentMethodAction> {}

export class UpdateIziPay extends ActionCreator<PaymentMethodAction> {}
export class UpdateIziPaySuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdateIziPayErrorFn extends ActionCreator<PaymentMethodAction> {}

export class UpdateCulqi extends ActionCreator<PaymentMethodAction> {}
export class UpdateCulqiSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdateCulqiErrorFn extends ActionCreator<PaymentMethodAction> {}

export class UpdateStripe extends ActionCreator<PaymentMethodAction> {}
export class UpdateStripeSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdateStripeErrorFn extends ActionCreator<PaymentMethodAction> {}

export class UpdatePaypal extends ActionCreator<PaymentMethodAction> {}
export class UpdatePaypalSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdatePaypalErrorFn extends ActionCreator<PaymentMethodAction> {}

export class UpdateTransbank extends ActionCreator<PaymentMethodAction> {}
export class UpdateTransbankSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdateTransbankErrorFn extends ActionCreator<PaymentMethodAction> {}

export class UpdateNiubizPayLink extends ActionCreator<PaymentMethodAction> {}
export class UpdateNiubizPayLinkSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdateNiubizPayLinkErrorFn extends ActionCreator<PaymentMethodAction> {}
export class UpdateNiubizWeb extends ActionCreator<PaymentMethodAction> {}
export class UpdateNiubizWebSuccess extends ActionCreator<PaymentMethodAction> {}
export class UpdateNiubizWebErrorFn extends ActionCreator<PaymentMethodAction> {}

export class DeletePaymentMethod extends ActionCreator<PaymentMethodAction> {}
export class DeletePaymentMethodSuccess extends ActionCreator<PaymentMethodAction> {}
export class DeletePaymentMethodErrorFn extends ActionCreator<PaymentMethodAction> {}

export class AddPaymentMethod extends ActionCreator<PaymentMethodAction> {}
export class AddPaymentMethodSuccess extends ActionCreator<PaymentMethodAction> {}
export class AddPaymentMethodErrorFn extends ActionCreator<PaymentMethodAction> {}

export class AddLogoPaymentMethod extends ActionCreator<PaymentMethodAction> {}
export class AddLogoPaymentMethodSuccess extends ActionCreator<PaymentMethodAction> {}
export class AddLogoPaymentMethodErrorFn extends ActionCreator<PaymentMethodAction> {}

export class GetPaymentMethods extends ActionCreator<PaymentMethodAction> {}
export class GetPaymentMethodsSuccess extends ActionCreator<PaymentMethodAction> {}
export class GetPaymentMethodsErrorFn extends ActionCreator<PaymentMethodAction> {}
export class PaymentMethodResetState extends ActionCreator<PaymentMethodAction> {}

export class GetPaymentMethod extends ActionCreator<PaymentMethodAction> {}
export class GetPaymentMethodSuccess extends ActionCreator<PaymentMethodAction> {}
export class GetPaymentMethodErrorFn extends ActionCreator<PaymentMethodAction> {}
export class ResetPartialState extends ActionCreator<PaymentMethodAction> {}

export const actions = {
  updatePaymentMethod: (payload: PaymentMethodAction) =>
    new UpdatePaymentMethod(payload),
  updateMercadoPagoCheckoutPro: (payload: PaymentMethodAction) =>
    new UpdateMercadoPagoCheckoutPro(payload),
  updateIziPay: (payload: PaymentMethodAction) => new UpdateIziPay(payload),
  updateCulqi: (payload: PaymentMethodAction) => new UpdateCulqi(payload),
  updateStripe: (payload: PaymentMethodAction) => new UpdateStripe(payload),
  updatePaypal: (payload: PaymentMethodAction) => new UpdatePaypal(payload),
  updateTransbank: (payload: PaymentMethodAction) =>
    new UpdateTransbank(payload),
  updateNiubizPayLink: (payload: PaymentMethodAction) =>
    new UpdateNiubizPayLink(payload),
  updateNiubizWeb: (payload: PaymentMethodAction) =>
    new UpdateNiubizWeb(payload),
  deletePaymentMethod: (payload: PaymentMethodAction) =>
    new DeletePaymentMethod(payload),
  resetState: () => new PaymentMethodResetState(),
  addPaymentMethod: (payload: PaymentMethodAction) =>
    new AddPaymentMethod(payload),
  getPaymentMethods: (payload: PaymentMethodAction) =>
    new GetPaymentMethods(payload),
  getPaymentMethod: (payload: PaymentMethodAction) =>
    new GetPaymentMethod(payload),
  resetPartialState: (payload: PaymentMethodAction) =>
    new ResetPartialState(payload),
  addLogoPaymentMethod: (payload: PaymentMethodAction) =>
    new AddLogoPaymentMethod(payload),
};

export const initialState = {};

export default createReducer<PaymentMethodConfigState>(initialState, {
  [UpdatePaymentMethod.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: false,
    error: undefined,
  }),
  [UpdatePaymentMethodSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      error: undefined,
      updateSuccess: true,
      paymentMethod: action.data,
      isSaving: false,
    };
  },
  [UpdatePaymentMethodErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: false,
    };
  },
  [UpdateMercadoPagoCheckoutPro.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateMercadoPagoCheckoutProSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
      // paymentMethod: action.data,
    };
  },
  [UpdateMercadoPagoCheckoutProErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [UpdateIziPay.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateIziPaySuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
    };
  },
  [UpdateIziPayErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [UpdateCulqi.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateCulqiSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
    };
  },
  [UpdateCulqiErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [UpdateStripe.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateStripeSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
    };
  },
  [UpdateStripeErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [UpdatePaypal.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdatePaypalSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
    };
  },
  [UpdatePaypalErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [UpdateTransbank.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateTransbankSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
    };
  },
  [UpdateTransbankErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [UpdateNiubizPayLink.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateNiubizPayLinkSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
    };
  },
  [UpdateNiubizPayLinkErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [UpdateNiubizWeb.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    updateSuccess: undefined,
    error: undefined,
  }),
  [UpdateNiubizWebSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const newPaymentMethods = [...(state.paymentMethods || [])];
    const index = newPaymentMethods.findIndex(p => p.id === action.data.id);
    if (index > -1) {
      newPaymentMethods[index] = action.data;
    }
    return {
      ...state,
      paymentMethods: newPaymentMethods,
      isSaving: undefined,
      updateSuccess: true,
    };
  },
  [UpdateNiubizWebErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    return {
      ...state,
      error: action.error,
      updateSuccess: false,
      isSaving: undefined,
    };
  },
  [DeletePaymentMethod.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isDeleting: true,
    deleteSuccess: false,
    error: undefined,
  }),
  [DeletePaymentMethodSuccess.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    error: undefined,
    deleteSuccess: true,
    isDeleting: false,
  }),
  [DeletePaymentMethodErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => ({
    ...state,
    error: action.error,
    deleteSuccess: false,
    isDeleting: false,
  }),
  [AddPaymentMethod.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    isSaving: true,
    error: undefined,
    addSuccess: false,
  }),
  [AddPaymentMethodSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => ({
    ...state,
    paymentMethods: [...(state.paymentMethods || []), action.data],
    error: undefined,
    addSuccess: true,
    paymentMethod: action.data,
    isSaving: false,
  }),
  [AddPaymentMethodErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => ({
    ...state,
    error: action.error,
    addSuccess: false,
    paymentMethod: undefined,
    isSaving: false,
  }),
  [GetPaymentMethods.type]: (state: PaymentMethodConfigState) => ({
    isLoading: true,
    paymentMethods: undefined,
    error: undefined,
  }),
  [GetPaymentMethodsSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => ({
    ...state,
    paymentMethods: action.data,
    isLoading: false,
  }),
  [GetPaymentMethodsErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => ({
    paymentMethods: [],
    error: state.error
      ? state.error
      : 'No se cargarón los métodos de pago. Intente de nuevo!',
    isLoading: false,
  }),
  [GetPaymentMethod.type]: (state: PaymentMethodConfigState) => {
    const {updateSuccess} = state;

    if (updateSuccess) {
      return {
        ...state,
      };
    } else {
      return {
        ...state,
        // paymentMethod: undefined,
        error: undefined,
        isFetching: true,
      };
    }
  },
  [GetPaymentMethodSuccess.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const {updateSuccess} = state;
    if (updateSuccess) {
      return {...state};
    } else {
      return {
        ...state,
        paymentMethod: action.data,
        isFetching: false,
      };
    }
  },
  [GetPaymentMethodErrorFn.type]: (
    state: PaymentMethodConfigState,
    action: PaymentMethodAction
  ) => {
    const {deleteSuccess} = state;
    return {
      ...state,
      paymentMethod: undefined,
      error: deleteSuccess ? '' : 'Error al obtener el método de pago',
      isFetching: false,
    };
  },
  [PaymentMethodResetState.type]: _ => initialState,
  [ResetPartialState.type]: (state: PaymentMethodConfigState) => ({
    ...state,
    paymentMethod: undefined,
    isLoading: false,
    deleteSuccess: undefined,
    updateSuccess: undefined,
    addSuccess: undefined,
    error: undefined,
  }),
});

export const getPaymentMethodEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(GetPaymentMethod.type),
    switchMap((action: PaymentMethodAction) => {
      return getPaymentMethodApi(undefined, {
        route: buildRoute(endpoints.GET_MY_PAYMENT_METHOD, [action.id]),
      }).pipe(
        map(
          (myPayment: PaymentMethod) =>
            new GetPaymentMethodSuccess({data: myPayment})
        ),
        buildCatchError(GetPaymentMethodErrorFn)
      );
    })
  );

export const getPaymentMethodsEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(
      GetPaymentMethods.type,
      DeletePaymentMethodSuccess.type,
      GetSessionSuccess.type,
      LoginSuccess.type
    ),
    switchMap((action: PaymentMethodAction) =>
      getPaymentMethodsApi().pipe(
        map(data => new GetPaymentMethodsSuccess({data: data})),
        buildCatchError(GetPaymentMethodsErrorFn)
      )
    )
  );
export const addPaymentMethodEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(AddPaymentMethod.type),
    switchMap(({paymentMethodRequest}: PaymentMethodAction) => {
      return addPaymentMethodApi(paymentMethodRequest).pipe(
        map(response => new AddPaymentMethodSuccess({data: response})),
        buildCatchError(AddPaymentMethodErrorFn)
      );
    })
  );

export const addLogoPaymentMethodEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(AddLogoPaymentMethod.type),
    switchMap((action: PaymentMethodAction) => {
      const {
        logoPaymentMethodRequest,
        paymentMethodRequest,
        qrCodePaymentMethodRequest,
      } = action;

      if (logoPaymentMethodRequest) {
        const files = [logoPaymentMethodRequest];

        return uploadImageStoreApi(
          createMultiFieldFormData(files, undefined, {
            filePrefix: 'checkout/payment-logo',
          })
        ).pipe(
          map((logoImages: StoredFile[]) => [
            logoImages,
            paymentMethodRequest,
            qrCodePaymentMethodRequest,
            undefined,
          ]),
          catchError(error => of([undefined, undefined, undefined, error]))
        );
      }

      return of([
        [],
        paymentMethodRequest,
        qrCodePaymentMethodRequest,
        undefined,
      ]);
    }),
    concatMap(
      ([
        logoImages,
        paymentMethodRequest,
        qrCodePaymentMethodRequest,
        error,
      ]) => {
        if (error) {
          return of(new AddPaymentMethodErrorFn({error}));
        }

        if (qrCodePaymentMethodRequest) {
          const files = [qrCodePaymentMethodRequest];

          return uploadImageStoreApi(
            createMultiFieldFormData(files, undefined, {
              filePrefix: 'checkout/payment-qr-code',
            })
          ).pipe(
            map((qrImages: StoredFile[]) => [
              logoImages,
              paymentMethodRequest,
              qrImages,
              undefined,
            ]),
            catchError(error => of([undefined, undefined, undefined, error]))
          );
        }

        return of([logoImages, paymentMethodRequest, [], undefined]);
      }
    ),
    concatMap(([logoImages, logoPaymentMethodRequest, qrImages, pipeError]) => {
      if (pipeError) {
        return of(new AddPaymentMethodErrorFn({error: pipeError}));
      }

      const newPaymentRequest: PaymentMethodRequest = {
        ...logoPaymentMethodRequest,
      };

      if (logoImages.length > 0) {
        newPaymentRequest.logoId = logoImages[0].id;
      }

      if (qrImages.length > 0) {
        newPaymentRequest.fileId = qrImages[0].id;
      }

      if (newPaymentRequest.id) {
        const route = buildRoute(endpoints.UPDATE_PAYMENT_METHOD, [
          logoPaymentMethodRequest.id,
        ]);

        const editPaymentRequest = {...newPaymentRequest};
        editPaymentRequest.id = undefined;

        return updatePaymentMethodApi(editPaymentRequest, {route}).pipe(
          map(response => new UpdatePaymentMethodSuccess({data: response})),
          buildCatchError(UpdatePaymentMethodErrorFn)
        );
      }

      return addPaymentMethodApi(newPaymentRequest).pipe(
        map(response => new AddPaymentMethodSuccess({data: response})),
        buildCatchError(AddPaymentMethodErrorFn)
      );
    })
  );

export const deletePaymentMethodEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(DeletePaymentMethod.type),
    switchMap((action: PaymentMethodAction) => {
      return deletePaymentMethodApi({
        route: buildRoute(endpoints.DELETE_PAYMENT_METHOD, [action.id]),
      }).pipe(
        map(() => new DeletePaymentMethodSuccess()),
        buildCatchError(DeletePaymentMethodErrorFn)
      );
    })
  );

export const updatePaymentMethodEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdatePaymentMethod.type),
    switchMap(
      ({
        paymentMethodRequest,
        typePaymentMethod,
        paymentMethodId,
      }: PaymentMethodAction) => {
        let api$: ActionsObservable;

        switch (typePaymentMethod) {
          case 'CONTINUE_TO_CHAT':
            api$ = updateContinueToChatApi({
              enabled: paymentMethodRequest.enabled,
            });
            break;
          case 'PAY_ON_DELIVERY':
            api$ = updatePayOnDeliveryApi({
              enabled: paymentMethodRequest.enabled,
            });
            break;
          default:
            api$ = updatePaymentMethodApi(paymentMethodRequest, {
              route: buildRoute(endpoints.UPDATE_PAYMENT_METHOD, [
                paymentMethodId,
              ]),
            });
        }

        return api$.pipe(
          map(response => new UpdatePaymentMethodSuccess({data: response})),
          buildCatchError(UpdatePaymentMethodErrorFn)
        );
      }
    )
  );

export const updateMercadoPagoCheckoutProEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateMercadoPagoCheckoutPro.type),
    switchMap(({mercadoPagoCheckoutProRequest}: PaymentMethodAction) =>
      updateMercadoPagoCheckoutProApi(mercadoPagoCheckoutProRequest).pipe(
        map(
          response => new UpdateMercadoPagoCheckoutProSuccess({data: response})
        ),
        buildCatchError(UpdateMercadoPagoCheckoutProErrorFn)
      )
    )
  );

export const updateIziPayEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateIziPay.type),
    switchMap(({iziPayRequest}: PaymentMethodAction) =>
      updateIziPayApi(iziPayRequest).pipe(
        map(response => new UpdateIziPaySuccess({data: response})),
        buildCatchError(UpdateIziPayErrorFn)
      )
    )
  );

export const updateCulqiEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateCulqi.type),
    switchMap(({culqiRequest}: PaymentMethodAction) =>
      updateCulqiApi(culqiRequest).pipe(
        map(response => new UpdateCulqiSuccess({data: response})),
        buildCatchError(UpdateCulqiErrorFn)
      )
    )
  );

export const updateStripeEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateStripe.type),
    switchMap(({stripeRequest}: PaymentMethodAction) =>
      updateStripeApi(stripeRequest).pipe(
        map(response => new UpdateStripeSuccess({data: response})),
        buildCatchError(UpdateStripeErrorFn)
      )
    )
  );

export const updatePaypalEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdatePaypal.type),
    switchMap(({paypalRequest}: PaymentMethodAction) =>
      updatePaypalApi(paypalRequest).pipe(
        map(response => new UpdatePaypalSuccess({data: response})),
        buildCatchError(UpdatePaypalErrorFn)
      )
    )
  );

export const updateTransbankEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateTransbank.type),
    switchMap(({transbankRequest}: PaymentMethodAction) =>
      updateTransbankApi(transbankRequest).pipe(
        map(response => new UpdateTransbankSuccess({data: response})),
        buildCatchError(UpdateTransbankErrorFn)
      )
    )
  );

export const updateNiubizPayLinkEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateNiubizPayLink.type),
    switchMap(({niubizRequest}: PaymentMethodAction) =>
      updateNiubizPayLinkApi(niubizRequest).pipe(
        map(response => new UpdateNiubizPayLinkSuccess({data: response})),
        buildCatchError(UpdateNiubizPayLinkErrorFn)
      )
    )
  );

export const updateNiubizWebEpic = (action$: ActionsObservable) =>
  action$.pipe(
    ofType(UpdateNiubizWeb.type),
    switchMap(({niubizRequest}: PaymentMethodAction) =>
      updateNiubizWebApi(niubizRequest).pipe(
        map(response => new UpdateNiubizWebSuccess({data: response})),
        buildCatchError(UpdateNiubizWebErrorFn)
      )
    )
  );
